import React, { useMemo } from 'react';
import { AuthContext, useAuthContext } from './AuthContextProvider';
import { DataContext, useDataContext } from './DataContextProvider';
import { AuthContextType, DataContextType } from './types';
import { createFieldMap, createFieldMapType } from './useEntities';

export const AvDataContextWithExternalProjectionsProvider = ({ children }) => {
  const { accountEntities, ...rest } = useDataContext();
  const value = useMemo(
    () => ({
      ...rest,
      accountEntities: {
        ...accountEntities,
        aggProjs: { ...accountEntities.aggProjs, ...accountEntities.externalProjs },
        fieldMap: { ...accountEntities.fieldMap, ...createFieldMap(accountEntities.externalProjs) },
        fieldTypeMap: { ...accountEntities.fieldTypeMap, ...createFieldMapType(accountEntities.externalProjs) },
      },
    }),
    [accountEntities, rest]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T;
export const OverrideAvContextProvider =
  (authContextOverride: DeepPartial<AuthContextType>, dataContextOverride: DeepPartial<DataContextType> = {}) =>
  ({ children }) => {
    const authContext = useAuthContext();
    const dataContext = useDataContext();

    const authValue = useMemo(() => ({ ...authContext, isLoading: false, ...authContextOverride }) as AuthContextType, [authContext]);
    const dataValue = useMemo(() => ({ ...dataContext, ...dataContextOverride }) as DataContextType, [dataContext]);

    return (
      <AuthContext.Provider value={authValue}>
        <DataContext.Provider value={dataValue}>{children}</DataContext.Provider>
      </AuthContext.Provider>
    );
  };

export const useAvContext = () => {
  const authContext = useAuthContext();
  const dataContext = useDataContext();

  return useMemo(
    () => ({
      ...authContext,
      ...dataContext,
      isLoading: authContext.isLoading || dataContext.isLoading,
    }),
    [authContext, dataContext]
  );
};
